import { computed, Injectable, signal } from '@angular/core';
import { IProfissional } from '@nomoh-monorepo/shared-types';

/**
 * Data Service para Profissional - Gestão de estado com Signals
 */
@Injectable({
  providedIn: 'root',
})
export class ProfissionalDataService {
  // State privado
  private _profissionais = signal<IProfissional[]>([]);
  private _profissionalAtual = signal<IProfissional | null>(null);
  private _loading = signal(false);
  private _erro = signal<string | null>(null);

  // Signals públicos (readonly)
  $profissionais = this._profissionais.asReadonly();
  $profissionalAtual = this._profissionalAtual.asReadonly();
  $loading = this._loading.asReadonly();
  $erro = this._erro.asReadonly();

  // Computed signals
  $profissionaisAtivos = computed(() =>
    this._profissionais().filter((p) => p.status === true)
  );

  $profissionaisNaQuota = computed(
    () =>
      this._profissionais().filter(
        (p) =>
          p.status !== false &&
          (p.perfilAcesso === 'PROFISSIONAL' || !p.perfilAcesso)
      ).length
  );

  $profissionaisPausados = computed(() =>
    this._profissionais().filter((p) => p.status === false)
  );

  $profissionaisOperacionais = computed(() =>
    this._profissionais().filter((p) => p.status !== false)
  );

  $totalProfissionais = computed(() => this._profissionais().length);

  // Setters
  setProfissionais(profissionais: IProfissional[]) {
    this._profissionais.set(this.normalizarProfissionais(profissionais));
  }

  setProfissionalAtual(profissional: IProfissional | null) {
    this._profissionalAtual.set(profissional);
  }

  addProfissional(profissional: IProfissional) {
    this._profissionais.update((current) =>
      this.normalizarProfissionais([...current, profissional])
    );
  }

  updateProfissional(id: string, data: Partial<IProfissional>) {
    this._profissionais.update((current) =>
      this.normalizarProfissionais(
        current.map((p) => (p.id === id ? { ...p, ...data } : p))
      )
    );

    if (this._profissionalAtual()?.id === id) {
      this._profissionalAtual.update((current) =>
        current ? { ...current, ...data } : null
      );
    }
  }

  removeProfissional(id: string) {
    this._profissionais.update((current) =>
      this.normalizarProfissionais(current.filter((p) => p.id !== id))
    );

    if (this._profissionalAtual()?.id === id) {
      this._profissionalAtual.set(null);
    }
  }

  setLoading(loading: boolean) {
    this._loading.set(loading);
  }

  setErro(erro: string | null) {
    this._erro.set(erro);
  }

  limparErro() {
    this._erro.set(null);
  }

  limpar() {
    this._profissionais.set([]);
    this._profissionalAtual.set(null);
    this._loading.set(false);
    this._erro.set(null);
  }

  private normalizarProfissionais(
    profissionais: IProfissional[]
  ): IProfissional[] {
    return [...(profissionais || [])].sort((a, b) =>
      a.nome.localeCompare(b.nome)
    );
  }
}
